import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import Row from "../../../common/components/Row"
import { useDialogContainer } from "../utils"
import DialogHeader from "./DialogHeader"
import InboxTemplateStatusChip from "./InboxTemplateStatusChip"
import RenderedEmailBody from "./RenderedEmailBody"

const APPROVALS_URL = "/crm/api/concierge/inbox-template-approvals/"

function mediumLabel(medium) {
  return medium === "sms" ? "SMS" : "Email"
}

function attachmentLabel(attachment) {
  const file = attachment.file || ""
  if (!file) {
    return `Attachment ${attachment.id}`
  }
  const parts = file.split("/")
  return decodeURIComponent(
    parts[parts.length - 1] || `Attachment ${attachment.id}`
  )
}

export default function ConciergeInboxTemplateViewDialog({
  open,
  template,
  onClose,
}) {
  const axios = useAuthorizedAxiosClient()
  const Container = useDialogContainer()
  const [detail, setDetail] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!open || !template) {
      setDetail(null)
      setError("")
      return
    }

    setIsLoading(true)
    setError("")
    axios
      .get(`${APPROVALS_URL}${template.medium}/${template.id}/`)
      .then(({ data }) => setDetail(data))
      .catch(({ response }) => {
        setDetail(null)
        setError(
          parseApiErrorMessage(
            response?.data,
            "Could not load template content"
          )
        )
      })
      .finally(() => setIsLoading(false))
  }, [axios, open, template])

  const title = template
    ? `View ${mediumLabel(template.medium)} template`
    : "View template"

  return (
    <Container
      open={open}
      onClose={onClose}
      anchor="right"
      maxWidth="md"
      fullWidth
    >
      <DialogHeader title={title} onClose={onClose} />
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <DialogContent>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : detail ? (
          <Column sx={{ gap: 2 }}>
            <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="h6" fontWeight={700}>
                {detail.name}
              </Typography>
              <Chip size="small" label={mediumLabel(detail.medium)} />
              <InboxTemplateStatusChip status={detail.approval_status} />
              {detail.include_signature ? (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label="Includes email signature"
                />
              ) : null}
            </Row>

            {detail.medium === "email" && detail.subject ? (
              <Column sx={{ gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Subject
                </Typography>
                <Typography variant="body2">{detail.subject}</Typography>
              </Column>
            ) : null}

            <Column sx={{ gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {detail.medium === "sms" ? "Message" : "Email body"}
              </Typography>
              {detail.medium === "email" ? (
                <RenderedEmailBody
                  renderedBody={detail.html_body || ""}
                  sx={{ maxHeight: "none", mt: 0 }}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  whiteSpace="pre-wrap"
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.default",
                  }}
                >
                  {detail.text || "No message content"}
                </Typography>
              )}
            </Column>

            {detail.attachments?.length ? (
              <Column sx={{ gap: 0.75 }}>
                <Typography variant="caption" color="text.secondary">
                  Attachments
                </Typography>
                {detail.attachments.map((attachment) => (
                  <Typography key={attachment.id} variant="body2">
                    {attachmentLabel(attachment)}
                  </Typography>
                ))}
              </Column>
            ) : null}
          </Column>
        ) : !isLoading ? (
          <Typography variant="body2" color="text.secondary">
            No template selected.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Container>
  )
}

export function useConciergeInboxTemplateViewDialogState() {
  const [viewTemplate, setViewTemplate] = useState(null)

  return {
    viewTemplate,
    viewDialogOpen: Boolean(viewTemplate),
    openViewDialog: setViewTemplate,
    closeViewDialog: () => setViewTemplate(null),
  }
}
